import { useState, useEffect } from 'react';
import { Calculator, Percent } from 'lucide-react';
import { CalculatorCard, SliderInput } from '../CalculatorCard';

export const NetSalaryCalculator = () => {
  const [grossSalary, setGrossSalary] = useState(50000);
  const [netSalary, setNetSalary] = useState(0);
  const [socialInsurance, setSocialInsurance] = useState(0);
  const [healthInsurance, setHealthInsurance] = useState(0);
  const [incomeTax, setIncomeTax] = useState(0);
  const [employerCost, setEmployerCost] = useState(0);

  useEffect(() => {
    // Czech tax calculation (simplified 2024)
    const socialRate = 0.065; // 6.5% employee
    const healthRate = 0.045; // 4.5% employee
    const taxRate = 0.15; // 15% income tax
    const taxCredit = 2570; // Monthly tax credit
    
    const social = Math.round(grossSalary * socialRate);
    const health = Math.round(grossSalary * healthRate);
    const taxBase = grossSalary;
    const taxBeforeCredit = Math.round(taxBase * taxRate);
    const tax = Math.max(0, taxBeforeCredit - taxCredit);
    const net = grossSalary - social - health - tax;
    
    // Employer cost (33.8% on top of gross)
    const employerSocial = Math.round(grossSalary * 0.248);
    const employerHealth = Math.round(grossSalary * 0.09);
    const totalEmployerCost = grossSalary + employerSocial + employerHealth;
    
    setSocialInsurance(social);
    setHealthInsurance(health);
    setIncomeTax(tax);
    setNetSalary(net);
    setEmployerCost(totalEmployerCost);
  }, [grossSalary]);

  const taxPercentage = Math.round(((grossSalary - netSalary) / grossSalary) * 100);

  const result = (
    <div className="space-y-6">
      <div className="text-right">
        <p className="text-gray-400 text-sm">Čistá mzda</p>
        <p className="text-4xl font-bold text-green">{netSalary.toLocaleString('cs-CZ')} Kč</p>
      </div>

      <div className="space-y-3">
        <div className="flex justify-between items-center p-3 bg-white/5 rounded-xl">
          <span className="text-gray-400">Hrubá mzda</span>
          <span className="text-white font-bold">{grossSalary.toLocaleString('cs-CZ')} Kč</span>
        </div>
        <div className="flex justify-between items-center p-3 bg-red-500/10 rounded-xl">
          <span className="text-gray-400">Sociální pojištění (6,5%)</span>
          <span className="text-red-400 font-bold">-{socialInsurance.toLocaleString('cs-CZ')} Kč</span>
        </div>
        <div className="flex justify-between items-center p-3 bg-red-500/10 rounded-xl">
          <span className="text-gray-400">Zdravotní pojištění (4,5%)</span>
          <span className="text-red-400 font-bold">-{healthInsurance.toLocaleString('cs-CZ')} Kč</span>
        </div>
        <div className="flex justify-between items-center p-3 bg-red-500/10 rounded-xl">
          <span className="text-gray-400">Daň z příjmu (15%)</span>
          <span className="text-red-400 font-bold">-{incomeTax.toLocaleString('cs-CZ')} Kč</span>
        </div>
        <div className="flex justify-between items-center p-3 bg-green/10 rounded-xl border border-green/30">
          <span className="text-white font-semibold">Čistá mzda</span>
          <span className="text-green font-bold">{netSalary.toLocaleString('cs-CZ')} Kč</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white/5 rounded-xl p-4">
          <p className="text-gray-400 text-xs">Celkové odvody</p>
          <p className="text-gold font-bold text-lg">{taxPercentage}%</p>
        </div>
        <div className="bg-white/5 rounded-xl p-4">
          <p className="text-gray-400 text-xs">Náklady zaměstnavatele</p>
          <p className="text-white font-bold text-lg">{employerCost.toLocaleString('cs-CZ')} Kč</p>
        </div>
      </div>
    </div>
  );

  return (
    <CalculatorCard
      id="mzda"
      title="Kalkulačka čisté mzdy"
      subtitle="Kolik vám zbyde z hrubé mzdy?"
      icon={Calculator}
      tip="Při vyjednávání o platu myslete v čisté mzdě. Zvýšení hrubé o 5000 Kč vám přinese jen ~3300 Kč čistého. Zvažte i benefity jako stravenky, home office nebo vzdělávání – ty nejsou zdaněny."
      result={result}
      showResult={true}
      testId="net-salary-calculator"
    >
      <SliderInput
        label="Hrubá měsíční mzda"
        value={grossSalary}
        onChange={setGrossSalary}
        min={17300}
        max={300000}
        step={1000}
        suffix="Kč"
        testId="salary-gross-slider"
      />
    </CalculatorCard>
  );
};
